/**
 * SHARED
 * Cross-cutting code usable by any module. Must not import from any module.
 */

// TODO: assign to correct module
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../utils/supabaseClient.js";
import { useSession } from "./SessionProvider.jsx";
import { useTheme } from "./ThemeProvider.jsx";
import { useBilling } from "../hooks/useBilling.js";
import { openBillingPortal } from "../utils/stripeLinks.js";
import { initialsOf, avatarUrl } from "../utils/userDisplay.js";
import { TRIAL_DAYS } from "../billingConfig.js";
import {
  listMyAccounts,
  listMembers,
  listPendingInvites,
  inviteMember,
  revokeInvite,
  changeMemberRole,
  removeMember,
  updateAccount,
  uploadAccountLogo,
} from "../utils/accountService.js";

const SECTIONS = [
  { id: "account", label: "Account" },
  { id: "password", label: "Password" },
  { id: "team", label: "Team" },
  { id: "appearance", label: "Appearance" },
  { id: "billing", label: "Billing" },
];

function initialTab() {
  const t = new URLSearchParams(window.location.search).get("tab");
  return SECTIONS.some((s) => s.id === t) ? t : "account";
}

export default function Settings() {
  const [tab, setTab] = useState(initialTab);
  return (
    <div className="settings-layout">
      <aside className="settings-nav">
        <h1>Account settings</h1>
        <nav>
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              className={`settings-navitem ${tab === s.id ? "active" : ""}`}
              onClick={() => setTab(s.id)}
            >
              {s.label}
            </button>
          ))}
        </nav>
      </aside>
      <div className="settings-content">
        {tab === "account" && <AccountSection />}
        {tab === "password" && <PasswordSection />}
        {tab === "team" && <TeamSection />}
        {tab === "appearance" && <AppearanceSection />}
        {tab === "billing" && <BillingSection />}
      </div>
    </div>
  );
}

function AccountSection() {
  const { user } = useSession();
  const [name, setName] = useState("");
  const [avatar, setAvatar] = useState("");
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    setName(user?.user_metadata?.full_name || user?.user_metadata?.name || "");
    setAvatar(avatarUrl(user));
  }, [user]);

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      setStatus({ ok: false, msg: "Use a PNG, JPG, or WebP image." });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setStatus({ ok: false, msg: "Image must be under 2 MB." });
      return;
    }
    setUploading(true);
    setStatus(null);
    try {
      const ext = file.type === "image/png" ? "png" : file.type === "image/webp" ? "webp" : "jpg";
      const path = `${user.id}/avatar.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("avatars")
        .upload(path, file, { upsert: true, contentType: file.type });
      if (upErr) throw upErr;
      const { data } = supabase.storage.from("avatars").getPublicUrl(path);
      const url = `${data.publicUrl}?t=${Date.now()}`; // cache-bust
      const { error: authErr } = await supabase.auth.updateUser({ data: { avatar_url: url } });
      if (authErr) throw authErr;
      await supabase.from("profiles").update({ avatar_url: url }).eq("id", user.id);
      setAvatar(url);
      setStatus({ ok: true, msg: "Photo updated." });
    } catch (err) {
      console.error("[settings] avatar upload failed:", err);
      setStatus({ ok: false, msg: err.message || "Upload failed." });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function removeAvatar() {
    setBusy(true);
    setStatus(null);
    try {
      await supabase.auth.updateUser({ data: { avatar_url: null } });
      await supabase.from("profiles").update({ avatar_url: null }).eq("id", user.id);
      setAvatar("");
      setStatus({ ok: true, msg: "Photo removed." });
    } catch (err) {
      setStatus({ ok: false, msg: err.message || "Could not remove photo." });
    } finally {
      setBusy(false);
    }
  }

  async function saveName(e) {
    e.preventDefault();
    setBusy(true);
    setStatus(null);
    try {
      const clean = name.trim().slice(0, 80);
      const { error: authErr } = await supabase.auth.updateUser({ data: { full_name: clean } });
      if (authErr) throw authErr;
      await supabase.from("profiles").update({ display_name: clean || null }).eq("id", user.id);
      setStatus({ ok: true, msg: "Saved." });
    } catch (err) {
      setStatus({ ok: false, msg: err.message || "Could not save changes." });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="settings-card">
      <h2>Account</h2>
      <div className="avatar-row">
        <div className="avatar-lg">
          {avatar ? <img src={avatar} alt="Your avatar" /> : initialsOf(user)}
        </div>
        <div>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            style={{ display: "none" }}
            onChange={handleUpload}
          />
          <button className="secondary" disabled={uploading} onClick={() => fileRef.current?.click()}>
            {uploading ? "Uploading…" : "Upload photo"}
          </button>
          {avatar && (
            <button className="link" disabled={busy} onClick={removeAvatar} style={{ marginLeft: 12 }}>
              Remove
            </button>
          )}
          <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>PNG, JPG, or WebP. Max 2 MB.</p>
        </div>
      </div>

      <form onSubmit={saveName}>
        <label>
          Full name
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" maxLength={80} autoComplete="name" />
        </label>
        <label>
          Email
          <input type="email" value={user?.email || ""} disabled />
        </label>
        <button className="btn" type="submit" disabled={busy}>{busy ? "Saving…" : "Save changes"}</button>
      </form>
      {status && <p className={status.ok ? "success" : "error"}>{status.msg}</p>}
    </section>
  );
}

function PasswordSection() {
  const { user } = useSession();
  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  async function submit(e) {
    e.preventDefault();
    if (password.length < 8) {
      setStatus({ ok: false, msg: "New password must be at least 8 characters." });
      return;
    }
    if (password !== confirm) {
      setStatus({ ok: false, msg: "New passwords don't match." });
      return;
    }
    if (password === current) {
      setStatus({ ok: false, msg: "New password must be different from your current one." });
      return;
    }
    setBusy(true);
    setStatus(null);
    try {
      // Re-verify the current password first. Supabase's updateUser() does NOT
      // check the old password, so without this an unattended open session could
      // silently change it. signInWithPassword just refreshes the same session.
      const { error: reauthErr } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: current,
      });
      if (reauthErr) {
        setStatus({ ok: false, msg: "Current password is incorrect." });
        return;
      }
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setStatus({ ok: true, msg: "Password updated." });
      setCurrent("");
      setPassword("");
      setConfirm("");
    } catch (err) {
      setStatus({ ok: false, msg: err.message || "Could not update password." });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="settings-card">
      <h2>Password</h2>
      <p className="muted">Change the password you use to log in. You'll need your current one.</p>
      <form onSubmit={submit}>
        <label>
          Current password
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
            autoComplete="current-password"
          />
        </label>
        <label>
          New password
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
          />
        </label>
        <label>
          Confirm new password
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
          />
        </label>
        <button className="btn" type="submit" disabled={busy}>{busy ? "Saving…" : "Update password"}</button>
      </form>
      {status && <p className={status.ok ? "success" : "error"}>{status.msg}</p>}
    </section>
  );
}

function AppearanceSection() {
  const { theme, setTheme } = useTheme();
  const options = [{ id: "light", label: "Light" }, { id: "dark", label: "Dark" }];
  return (
    <section className="settings-card">
      <h2>Appearance</h2>
      <p className="muted">Choose how Brand Command looks. Applies across every module.</p>
      <div className="theme-options">
        {options.map((opt) => (
          <button
            key={opt.id}
            type="button"
            className={`theme-option ${theme === opt.id ? "active" : ""}`}
            aria-pressed={theme === opt.id}
            onClick={() => setTheme(opt.id)}
          >
            <span className={`theme-swatch ${opt.id}`} aria-hidden="true" />
            <span>{opt.label}</span>
            {theme === opt.id && <span className="theme-check" aria-hidden="true">✓</span>}
          </button>
        ))}
      </div>
    </section>
  );
}

function BillingSection() {
  const { subscription, trialDaysLeft, isPro, loading } = useBilling();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function go(fn, arg) {
    setBusy(true);
    setErr(null);
    try {
      await fn(arg);
    } catch (e) {
      setErr(e.message || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <section className="settings-card"><h2>Billing</h2><p className="muted">Loading…</p></section>;

  return (
    <section className="settings-card">
      <h2>Billing</h2>
      {subscription ? (
        <>
          <p>
            Plan: <strong>{isPro ? "Growth" : "Essential"}</strong> · Status:{" "}
            <strong>{subscription.status}</strong>
            {subscription.status === "trialing" && trialDaysLeft != null && (
              <> · {trialDaysLeft} day{trialDaysLeft === 1 ? "" : "s"} left in trial</>
            )}
          </p>
          <button className="btn" disabled={busy} onClick={() => go(openBillingPortal)}>Manage billing</button>
        </>
      ) : (
        <>
          <p className="muted">Start your {TRIAL_DAYS}-day free trial — no credit card required.</p>
          <div className="plan-row">
            {/* Plan choice depends on tier x seat band x interval, so it lives
                in one place (/subscribe) rather than being duplicated here. */}
            <button className="btn" disabled={busy} onClick={() => navigate("/subscribe")}>
              Choose a plan
            </button>
          </div>
        </>
      )}
      {err && <p className="error" style={{ marginTop: 12 }}>{err}</p>}
    </section>
  );
}

function TeamSection() {
  const { user } = useSession();
  const [loading, setLoading] = useState(true);
  const [account, setAccount] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [logoUrl, setLogoUrl] = useState(null);
  const [primaryColor, setPrimaryColor] = useState("");
  const [secondaryColor, setSecondaryColor] = useState("");
  const logoRef = useRef(null);

  async function refresh(acct) {
    const a = acct || account;
    if (!a) return;
    const [m, inv] = await Promise.all([listMembers(a.accountId), listPendingInvites(a.accountId)]);
    setMembers(m);
    setInvites(inv);
  }

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const accounts = await listMyAccounts();
        // Manage the first workspace you own/admin (your personal one by default).
        const managed = accounts.find((a) => a.role === "owner" || a.role === "admin") || accounts[0];
        if (!alive) return;
        setAccount(managed || null);
        setCompanyName(managed?.name || "");
        setLogoUrl(managed?.logoUrl || null);
        setPrimaryColor(managed?.primaryColor || "");
        setSecondaryColor(managed?.secondaryColor || "");
        if (managed) await refresh(managed);
      } catch (e) {
        if (alive) setStatus({ ok: false, msg: e.message || "Could not load your team." });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const canManage = account && (account.role === "owner" || account.role === "admin");

  async function handleInvite(e) {
    e.preventDefault();
    setBusy(true);
    setStatus(null);
    try {
      await inviteMember(account.accountId, email, role);
      setEmail("");
      await refresh();
      setStatus({ ok: true, msg: "Invite sent — they'll join when they sign up or log in with that email." });
    } catch (err) {
      setStatus({ ok: false, msg: err.message });
    } finally {
      setBusy(false);
    }
  }

  async function act(fn) {
    setBusy(true);
    setStatus(null);
    try {
      await fn();
      await refresh();
    } catch (err) {
      setStatus({ ok: false, msg: err.message });
    } finally {
      setBusy(false);
    }
  }

  async function saveCompanyName() {
    setBusy(true);
    setStatus(null);
    try {
      await updateAccount(account.accountId, { name: companyName });
      setAccount((a) => ({ ...a, name: companyName.trim() }));
      setStatus({ ok: true, msg: "Workspace name saved." });
    } catch (err) {
      setStatus({ ok: false, msg: err.message });
    } finally {
      setBusy(false);
    }
  }

  // Brand colors save the instant the user picks one (no Save button).
  // These are the swatches shown when a tenant has NEVER set a brand colour, so
  // they must match what the exports actually render in that same unset case —
  // otherwise the picker previews one thing and the PDF prints another.
  // Every branded export reads primary_color as the MAJOR colour (the navy
  // header band) and secondary_color as the MINOR/accent colour: see
  // `BRAND_DEFAULT` / `ACCENT_DEFAULT` in specPdf.js + incentivePdf.js and
  // `NAVY` / `ACCENT` in specExport.js.
  // ⚠️ These two were SWAPPED (major #E85D26 orange, minor #1B2A4A navy) — the
  // exact inverse — so an untouched tenant saw both swatches backwards from
  // their own PDF. Fixed 2026-08-07; also retires the #E85D26 the app no longer
  // uses anywhere on screen. Display-only fallbacks: nothing is written to
  // `accounts` until the user actually picks, so no existing tenant changed.
  // (backlog 321)
  const DEFAULT_PRIMARY = "#1B2A4A";   // major — matches BRAND_DEFAULT / NAVY
  const DEFAULT_SECONDARY = "#F2A33C"; // minor — matches ACCENT_DEFAULT / ACCENT
  async function saveColor(field, value) {
    const setter = field === "primary_color" ? setPrimaryColor : setSecondaryColor;
    const prev = field === "primary_color" ? primaryColor : secondaryColor;
    setter(value);
    setStatus(null);
    try {
      await updateAccount(account.accountId, { [field]: value });
      setStatus({ ok: true, msg: "Brand colors saved." });
    } catch (err) {
      setter(prev);
      setStatus({ ok: false, msg: err.message });
    }
  }

  async function handleLogo(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setStatus(null);
    try {
      const url = await uploadAccountLogo(account.accountId, file);
      setLogoUrl(url);
      setStatus({ ok: true, msg: "Logo updated." });
    } catch (err) {
      setStatus({ ok: false, msg: err.message });
    } finally {
      setBusy(false);
      if (logoRef.current) logoRef.current.value = "";
    }
  }

  const rowStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    padding: "10px 0",
    borderBottom: "1px solid rgba(128,128,128,.18)",
  };
  const listStyle = { listStyle: "none", padding: 0, margin: 0 };

  if (loading) {
    return <section className="settings-card"><h2>Team</h2><p className="muted">Loading…</p></section>;
  }
  if (!account) {
    return <section className="settings-card"><h2>Team</h2><p className="muted">No workspace found.</p></section>;
  }

  return (
    <section className="settings-card">
      <h2>Team</h2>
      <p className="muted">Your company workspace — its members share these maps and the plan.</p>

      {canManage ? (
        <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap", margin: "10px 0 6px" }}>
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Company logo"
              style={{ maxHeight: 64, maxWidth: 200, width: "auto", height: "auto", objectFit: "contain", display: "block", borderRadius: 8, flex: "0 0 auto" }}
            />
          ) : (
            <div style={{ width: 64, height: 64, borderRadius: 12, background: "var(--sunken, #eef0f4)", display: "grid", placeItems: "center", flex: "0 0 auto" }}>
              <span className="muted" style={{ fontSize: 11 }}>No logo</span>
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: "1 1 240px" }}>
            <div style={{ display: "flex", flexDirection: "row", gap: 8 }}>
              <input value={companyName} onChange={(e) => setCompanyName(e.target.value)} maxLength={120} aria-label="Company name" placeholder="Company name" style={{ flex: "1 1 auto", minWidth: 0, margin: 0 }} />
              <button className="btn" type="button" disabled={busy || !companyName.trim()} onClick={saveCompanyName} style={{ flex: "0 0 auto" }}>Save</button>
            </div>
            <div>
              <input ref={logoRef} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: "none" }} onChange={handleLogo} />
              <button type="button" className="secondary" disabled={busy} onClick={() => logoRef.current?.click()}>Upload logo</button>
              <span className="muted" style={{ fontSize: 12, marginLeft: 10 }}>PNG, JPG, WebP · max 2 MB</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap", marginTop: 2 }}>
              <label style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8, fontSize: 13, margin: 0 }}>
                <input type="color" value={primaryColor || DEFAULT_PRIMARY} disabled={busy}
                       onChange={(e) => saveColor("primary_color", e.target.value)}
                       title="Major brand color"
                       style={{ width: 38, height: 28, padding: 0, border: "1px solid var(--border)", borderRadius: 6, background: "none", cursor: "pointer" }} />
                Major color
              </label>
              <label style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8, fontSize: 13, margin: 0 }}>
                <input type="color" value={secondaryColor || DEFAULT_SECONDARY} disabled={busy}
                       onChange={(e) => saveColor("secondary_color", e.target.value)}
                       title="Minor brand color"
                       style={{ width: 38, height: 28, padding: 0, border: "1px solid var(--border)", borderRadius: 6, background: "none", cursor: "pointer" }} />
                Minor color
              </label>
              <span className="muted" style={{ fontSize: 12 }}>Used to brand your exports (e.g. incentive PDFs).</span>
            </div>
          </div>
        </div>
      ) : (
        <p style={{ display: "flex", alignItems: "center", gap: 10, margin: "10px 0" }}>
          {logoUrl && <img src={logoUrl} alt="" style={{ width: 32, height: 32, borderRadius: 7, objectFit: "cover" }} />}
          <strong>{account.name}</strong>
        </p>
      )}

      {canManage && <h3 style={{ fontSize: 14, margin: "18px 0 8px" }}>Invite a teammate</h3>}

      {canManage && (
        <form onSubmit={handleInvite} style={{ display: "flex", flexDirection: "row", gap: 10, alignItems: "center", margin: "0 0 22px" }}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="teammate@brand.com"
            aria-label="Invite by email"
            autoComplete="off"
            style={{ flex: "1 1 auto", minWidth: 0, margin: 0 }}
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            aria-label="Role"
            style={{ flex: "0 0 auto", width: "auto", margin: 0 }}
          >
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
          <button className="btn" type="submit" disabled={busy || !email} style={{ flex: "0 0 auto", alignSelf: "auto", whiteSpace: "nowrap" }}>
            Send invite
          </button>
        </form>
      )}

      <h3 style={{ fontSize: 14, margin: "18px 0 8px" }}>Members</h3>
      <ul style={listStyle}>
        {members.map((m) => {
          const isSelf = m.user_id === user?.id;
          const isOwner = m.role === "owner";
          return (
            <li key={m.user_id} style={rowStyle}>
              <span>{m.email}{isSelf && <span className="muted"> (you)</span>}</span>
              <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {canManage && !isOwner && !isSelf ? (
                  <select value={m.role} disabled={busy} onChange={(e) => act(() => changeMemberRole(account.accountId, m.user_id, e.target.value))}>
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                  </select>
                ) : (
                  <span className="muted" style={{ textTransform: "capitalize" }}>{m.role}</span>
                )}
                {canManage && !isOwner && !isSelf && (
                  <button className="link" disabled={busy} onClick={() => act(() => removeMember(account.accountId, m.user_id))}>Remove</button>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      {invites.length > 0 && (
        <>
          <h3 style={{ fontSize: 14, margin: "18px 0 8px" }}>Pending invites</h3>
          <ul style={listStyle}>
            {invites.map((i) => (
              <li key={i.id} style={rowStyle}>
                <span>{i.email}</span>
                <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span className="muted" style={{ textTransform: "capitalize" }}>{i.role} · pending</span>
                  {canManage && <button className="link" disabled={busy} onClick={() => act(() => revokeInvite(i.id))}>Revoke</button>}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {status && <p className={status.ok ? "success" : "error"} style={{ marginTop: 14 }}>{status.msg}</p>}
    </section>
  );
}
